import { _ } from "../../vendor";
import { CollectionElement } from "js/elements/base/CollectionElement";
import * as geom from "js/core/utilities/geom";
import ConnectorGroup from "js/elements/elements/connectors/ConnectorGroup";
import {
    BulletTextNodeElement,
    ContentAndTextNodeElement,
    ContentNodeElement,
    FlexCircleNodeElement,
    NumberedTextNodeElement,
    TextInShapeNodeElement,
    TextNodeElement
} from "js/elements/base/NodeElement";
import { AnimationSequenceType, NodeType } from "common/constants";

export class AnnotationLayer extends CollectionElement {
    get minItemCount() {
        return 0;
    }

    get allowDragDropElements() {
        return false;
    }

    get canSelect() {
        return false;
    }

    getChildItemType(model) {
        switch (model.nodeType) {
            case NodeType.FLEX_CIRCLE:
                return FlexCircleNodeElement;
            case NodeType.BOX:
            case NodeType.CAPSULE:
            case NodeType.DIAMOND:
            case NodeType.CIRCLE:
                return TextInShapeNodeElement;
            case NodeType.BULLET_TEXT:
                return BulletTextNodeElement;
            case NodeType.TEXT:
                return TextNodeElement;
            case NodeType.CONTENT_AND_TEXT:
                return ContentAndTextNodeElement;
            case NodeType.CONTENT:
                return ContentNodeElement;
            case NodeType.NUMBERED_TEXT:
                return NumberedTextNodeElement;
            default:
                return TextInShapeNodeElement;
        }
    }

    getAllowedNodeTypes() {
        return [NodeType.TEXT, NodeType.BOX, NodeType.CAPSULE, NodeType.CIRCLE, NodeType.BULLET_TEXT, NodeType.CONTENT_AND_TEXT, NodeType.NUMBERED_TEXT, NodeType.CONTENT, NodeType.LINE];
    }

    getNodeSelectionUIType(node) {
        return "AnnotationItemSelection";
    }

    getNodeRolloverUIType(node) {
        return "AnnotationItemRollover";
    }

    getChildOptions(model) {
        return { allowConnection: false };
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition, true);
    }

    get constrainNodesToBounds() {
        return false;
    }

    get canRefreshElement() {
        return true;
    }

    _build() {
        super._build();

        if (!this.model.connections) {
            this.model.connections = {
                items: []
            };
        }

        this.connectors = this.addElement("connectors", () => ConnectorGroup, {
            model: this.model.connections,
            containerElement: this,
            startPointsAreLocked: true
        });
        this.connectors.layer = -1;
    }

    _calcProps(props, options) {
        const { size, children, styles } = props;

        let shouldUpdateModel = false;
        const containerBounds = new geom.Rect(0, 0, size);
        for (const item of this.itemElements) {
            const itemProps = children.setProps(item, size);
            if (item.model.snapOptions) {
                const sourceElement = this.canvas.getElementByUniquePath(item.model.snapOptions.elementId);
                const snapPoint = sourceElement.getSnapPoint(item);
                itemProps.bounds = new geom.Rect(snapPoint.x, snapPoint.y, itemProps.size);
            } else {
                itemProps.bounds = new geom.Rect(size.width * item.model.x, size.height * item.model.y, itemProps.size);
            }

            // adjustedItemBounds will respect the node's registration point
            const adjustedItemBounds = item.bounds;
            // Fitting the node
            if (this.constrainNodesToBounds && !containerBounds.contains(adjustedItemBounds)) {
                const offset = adjustedItemBounds.fitInRect(containerBounds).position.minus(adjustedItemBounds.position);
                itemProps.bounds = itemProps.bounds.offset(offset);
                item.model.x += offset.x / size.width;
                item.model.y += offset.y / size.height;
                // If the item is being dragged, then its model will be updated
                // when dragging is finished
                if (!item.isDragging && !item.isDragResizing) {
                    shouldUpdateModel = true;
                }
            }
        }

        if (shouldUpdateModel) {
            this.canvas.layouter.runPostRender(() => this.canvas.saveCanvasModel());
        }

        const connectorProps = children.setProps(this.connectors, size);
        connectorProps.bounds = new geom.Rect(0, 0, size);

        return { size };
    }

    deleteItem(itemId) {
        // delete any connectors that referenced the deleted item
        _.remove(this.model.connections.items, connector => connector.source == itemId);
        super.deleteItem(itemId);
    }

    registerAnimations(animationManager) {
        this.itemElements.forEach((element, idx) => {
            const elementName = `${element.buildName}-${idx + 1}`;

            const connectorsBeforeElement = element.connectorsFromNode.length && element.connectorsFromNode[0].endDecoration == "circle";
            if (connectorsBeforeElement) {
                this.buildConnectorAnimations(animationManager, this.connectors.itemElements.filter(c => c.model.source == element.id), "-> " + elementName, true);
            }

            const elementSeq = animationManager.findOrCreateAnimationSequence(element.id, elementName);
            for (const animation of element.buildAnimations) {
                elementSeq.addAnimation(animation);
            }

            if (!connectorsBeforeElement) {
                this.buildConnectorAnimations(animationManager, this.connectors.itemElements.filter(c => c.model.source == element.id), "-> " + elementName, false);
            }
        });

        // Building animations for connectors that don't belong to the annotation nodes
        // i.e. "change in value" connectors
        this.connectors.itemElements
            .filter(connector => !this.itemElements.some(element => connector.model.source === element.id))
            .forEach(connector => {
                this.buildConnectorAnimations(animationManager, [connector], connector.buildName);
            });
    }

    buildConnectorAnimations(animationManager, connectors, name, reverseDirection) {
        if (connectors.length) {
            const sequence = animationManager.findOrCreateAnimationSequence(name, name);
            const pathLength = _.maxBy(connectors, c => c.connectorPath.length).connectorPath.length;
            for (const connector of connectors) {
                sequence.addAnimation({
                    type: AnimationSequenceType.ANIMATE_TO_VALUE,
                    target: connector,
                    duration: duration => pathLength * 5 * (duration / 1000),
                    prepare: () => {
                        connector.animationState = { drawPercentage: 0 };
                    },
                    callback: value => {
                        connector.animationState = { drawPercentage: value, reverseDirection };
                    },
                    reset: () => {
                        connector.animationState = { drawPercentage: 1 };
                    },
                    startValue: 0,
                    endValue: 1,
                    easing: "linear"
                });
            }
        }
    }

    async pasteFromClipboard({ dataType, model }) {
        if (dataType === "element") {
            const modelToPaste = _.cloneDeep(model);
            while (this.itemElements.filter(item => item.model.x === modelToPaste.x && item.model.y === modelToPaste.y).length > 0) {
                modelToPaste.x += 0.05;
                modelToPaste.y += 0.05;
            }
            delete modelToPaste.id;

            const { id } = this.addItem(modelToPaste);
            await this.canvas.updateCanvasModel(false);
            return this.getChild(id);
        }
    }
}

export const elements = {
    AnnotationLayer
};
